import { Box, Button, Drawer, Typography } from '@mui/material';
import CloseButton from 'components/@home/drawers/CloseButton';
import { useDrawer } from 'components/common/Drawer/DrawerContext';
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { Translate } from 'utils/i18n';

const DrawerContent = forwardRef(
  (
    { drawer, title, actions = undefined, children, onSubmit = null, onClose = null, sx = {} },
    ref,
  ) => {
    const onSubmitRef = useRef(onSubmit);
    const { visible, show, hide, state } = useDrawer(drawer);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const handleOnClose = useCallback(() => {
      hide();
      if (onClose) {
        onClose();
      }
    }, [hide, onClose]);
    const handleSubmit = useCallback(
      e => {
        e.preventDefault();
        e.stopPropagation();
        if (onSubmitRef.current) {
          setIsSubmitting(true);
          const result = onSubmitRef.current();
          if (result instanceof Promise) {
            result.finally(() => {
              setIsSubmitting(false);
            });
          } else {
            setIsSubmitting(false);
          }
        } else {
          hide();
        }
      },
      [hide],
    );
    useImperativeHandle(ref, () => ({
      visible,
      show,
      hide,
      state,
      setOnSubmit: newOnSubmit => {
        onSubmitRef.current = newOnSubmit;
      },
    }));
    return (
      <Drawer anchor="right" open={visible} onClose={handleOnClose}>
        <Box sx={{ display: 'flex', ...sx }}>
          <Box sx={{ paddingTop: 2.5, paddingLeft: 2.5 }}>
            <CloseButton onClick={hide} />
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              flexGrow: 1,
              height: '100vh',
            }}
            role="none"
          >
            <Box
              component="form"
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
              onSubmit={handleSubmit}
            >
              <Typography variant="h5" sx={{ mt: 2.5, ml: 2, mb: 2 }}>
                {title}
              </Typography>
              <Box
                sx={{
                  px: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  flexGrow: 1,
                  overflowY: 'scroll',
                  overflowX: 'hidden',
                }}
              >
                {children}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  padding: 2,
                  gap: 1,
                }}
              >
                {typeof actions === 'function'
                  ? actions({ visible, show, hide, state })
                  : actions || (
                      <>
                        <Button type="button" variant="outlined" color="secondary" onClick={hide}>
                          <Translate value="Cancel" />
                        </Button>
                        <Button type="submit" variant="contained" disabled={isSubmitting}>
                          <Translate value="Save" />
                        </Button>
                      </>
                    )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Drawer>
    );
  },
);

DrawerContent.propTypes = {
  drawer: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  actions: PropTypes.node,
  children: PropTypes.node.isRequired,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
  sx: PropTypes.object,
};

export default DrawerContent;
